import { ArenaGameState } from '../../state/ArenaTypes';
import { GameTableConfig } from '../../engine/tableConfig/types';
import { ScoreDisplayResult, BooksToSetResult, HandScoreResult } from '../common/IGameRules';
import { IScorePresenter } from '../common/IScorePresenter';

/**
 * Presentation-only score formatter for Hearts (Point Penalty scoring).
 *
 * The server is authoritative for all scoring, penalty points,
 * trick counts, moon shooting, outcomes, and match state.
 *
 * This class only formats values already present in authoritative state.
 */
export class HeartsScorePresenter implements IScorePresenter {
  public formatScoreDisplay(gameState: ArenaGameState, config: GameTableConfig): ScoreDisplayResult {
    const playerScores: number[] = (gameState as any).playerScores ?? (gameState as any).seatScores ?? [
      gameState.team1Score ?? 0,
      gameState.team2Score ?? 0,
      0,
      0
    ];
    return {
      team1Label: String(playerScores[0] ?? 0),
      team2Label: String(playerScores[1] ?? 0),
      team1Details: `P1: ${playerScores[0] ?? 0} | P2: ${playerScores[1] ?? 0}`,
      team2Details: `P3: ${playerScores[2] ?? 0} | P4: ${playerScores[3] ?? 0}`,
      targetScoreLabel: `Target: ${config.scoring.targetScore}`
    };
  }

  public calculateBooksToSet(_gameState: ArenaGameState): BooksToSetResult {
    return {
      relevantTeam: null,
      booksNeededToSet: 0,
      booksToSetTarget: 0,
      defendingBooksWon: 0,
      declarerTeam: null,
      teams: {}
    };
  }

  public formatHandFanStat(seatIdx: number, gameState: ArenaGameState): string {
    const seatPoints = Array.isArray(gameState?.seatPoints)
      ? gameState.seatPoints
      : (Array.isArray((gameState as any)?.pointsTaken)
        ? (gameState as any).pointsTaken
        : (Array.isArray((gameState?.gameExt as any)?.seatPoints)
          ? (gameState.gameExt as any).seatPoints
          : null));
    const pts = seatPoints ? (Number(seatPoints[seatIdx]) || 0) : 0;
    return pts === 0 ? '0 points taken' : `${pts} ${pts === 1 ? 'point' : 'points'} taken`;
  }

  public calculateHandScore(gameState: ArenaGameState, _config: GameTableConfig): HandScoreResult {
    const history = Array.isArray(gameState.scoreHistory) ? gameState.scoreHistory : [];
    const latest = history.find(h => h && h.handNumber === gameState.handNumber) || (history.length > 0 ? history[history.length - 1] : null);

    if (latest) {
      return {
        teamScores: {
          1: latest.team1Score ?? gameState.team1Score ?? 0,
          2: latest.team2Score ?? gameState.team2Score ?? 0
        },
        teamDeltas: {
          1: latest.pointsTeam1 ?? 0,
          2: latest.pointsTeam2 ?? 0
        },
        contracts: [],
        summary: latest.summary || gameState.lastActionMessage || '',
        isSet: false,
        setTeam: null,
        celebration: gameState.shotTheMoonSeat != null
          ? {
              type: 'sweep',
              isUserTeam: false,
              winningTeam: Number(gameState.shotTheMoonSeat),
              title: 'Shot the Moon!'
            }
          : null
      };
    }

    return {
      teamScores: {
        1: gameState.team1Score ?? 0,
        2: gameState.team2Score ?? 0
      },
      teamDeltas: {
        1: 0,
        2: 0
      },
      contracts: [],
      summary: gameState.lastActionMessage || '',
      isSet: false,
      setTeam: null,
      celebration: null
    };
  }
}
